<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { UiIcon, UiText } from '@shopexperts/shared';
import type { ExpertTag } from '@shopexperts/legacy-api';
import { useExpertTagCatalog, useExpertTagsMutation } from '@/app/composables/expert-manager/useExpertTags';

const props = defineProps<{
  expertId: number;
  tags: ExpertTag[];
}>();

const { tags: catalog } = useExpertTagCatalog();
const { syncTags } = useExpertTagsMutation();

const isPending = computed(() => syncTags.isPending.value);
const currentNames = computed(() => props.tags.map((tag) => tag.name));

const isAdding = ref(false);
const draft = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);

onClickOutside(containerRef, () => { closeAdd(); });

const normalizedDraft = computed(() => draft.value.replace(/\s+/g, ' ').trim());

const suggestions = computed(() => {
  const assigned = new Set(currentNames.value.map((name) => name.toLowerCase()));
  const query = normalizedDraft.value.toLowerCase();
  return catalog.value
    .filter((tag) => !assigned.has(tag.name.toLowerCase()))
    .filter((tag) => !query || tag.name.toLowerCase().includes(query))
    .slice(0, 8);
});

/** The API creates unknown names on the fly, so offering it inline is enough. */
const canCreate = computed(() => {
  if (!normalizedDraft.value || normalizedDraft.value.length > 50) return false;
  const known = new Set(catalog.value.map((tag) => tag.name.toLowerCase()));
  const assigned = new Set(currentNames.value.map((name) => name.toLowerCase()));
  const value = normalizedDraft.value.toLowerCase();
  return !known.has(value) && !assigned.has(value);
});

async function openAdd() {
  isAdding.value = true;
  draft.value = '';
  await nextTick();
  inputRef.value?.focus();
}

function closeAdd() {
  isAdding.value = false;
  draft.value = '';
}

async function replaceTags(names: string[]) {
  await syncTags.mutateAsync({ expertId: props.expertId, tags: names });
}

async function addTag(name: string) {
  const normalized = name.replace(/\s+/g, ' ').trim();
  if (!normalized) return;
  if (currentNames.value.some((existing) => existing.toLowerCase() === normalized.toLowerCase())) {
    closeAdd();
    return;
  }
  if (currentNames.value.length >= 25) return;
  await replaceTags([...currentNames.value, normalized]);
  draft.value = '';
  await nextTick();
  inputRef.value?.focus();
}

async function removeTag(name: string) {
  await replaceTags(currentNames.value.filter((existing) => existing !== name));
}

function onEnter() {
  // An exact name always wins over an alphabetically-earlier partial match, so
  // typing a full existing tag name assigns that tag rather than a neighbour.
  const exact = suggestions.value.find(
    (tag) => tag.name.toLowerCase() === normalizedDraft.value.toLowerCase(),
  );
  if (exact) {
    addTag(exact.name);
    return;
  }
  const first = suggestions.value[0];
  if (canCreate.value || !first) addTag(normalizedDraft.value);
  else addTag(first.name);
}
</script>

<template>
  <div ref="containerRef" class="relative">
    <div class="flex flex-wrap items-center gap-1.5">
      <span
        v-for="tag in tags"
        :key="tag.id"
        class="group inline-flex items-center gap-1 rounded-md border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
      >
        {{ tag.name }}
        <button
          type="button"
          class="opacity-40 transition-opacity hover:opacity-90 disabled:opacity-20 cursor-pointer"
          :disabled="isPending"
          :aria-label="`Remove tag ${tag.name}`"
          @click="removeTag(tag.name)"
        >
          <UiIcon name="close" class="h-3 w-3" />
        </button>
      </span>

      <div
        v-if="isAdding"
        class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5
               focus-within:border-se-highlight focus-within:ring-2 focus-within:ring-se-highlight/30"
      >
        <input
          ref="inputRef"
          v-model="draft"
          type="text"
          maxlength="50"
          aria-label="Tag name"
          placeholder="Tag name"
          class="w-36 text-[11px] text-se-dark outline-none"
          :disabled="isPending"
          @keydown.enter.prevent="onEnter"
          @keydown.esc.prevent="closeAdd"
        />
      </div>

      <button
        v-else
        type="button"
        class="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700 disabled:opacity-40 cursor-pointer"
        :disabled="isPending || tags.length >= 25"
        @click="openAdd"
      >
        <UiIcon name="plus" class="h-3 w-3" />
        Add tag
      </button>
    </div>

    <!-- Suggestions -->
    <div
      v-if="isAdding && (suggestions.length > 0 || canCreate)"
      class="absolute left-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
    >
      <button
        v-for="tag in suggestions"
        :key="tag.id"
        type="button"
        class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left transition-colors hover:bg-gray-50"
        @click="addTag(tag.name)"
      >
        <UiText as="span" variant="p-small" class="truncate text-gray-700">{{ tag.name }}</UiText>
        <UiText as="span" variant="caption-normal" class="shrink-0 text-gray-400">
          {{ tag.experts_count }}
        </UiText>
      </button>

      <template v-if="canCreate">
        <div v-if="suggestions.length" class="my-1 border-t border-gray-100" />
        <button
          type="button"
          class="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-gray-50"
          @click="addTag(normalizedDraft)"
        >
          <UiIcon name="plus" class="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <UiText as="span" variant="p-small" class="truncate text-gray-700">
            Create “{{ normalizedDraft }}”
          </UiText>
        </button>
      </template>
    </div>
  </div>
</template>
